/**
 * hooks/useAdmin.ts
 * Author: Sean Johnson <sean@scj.net>
 * Purpose: Admin API client for user and group management
 * Last Updated: 2026-05-31
 * Version: 1.0.0
 */

const API = '/api/auth/admin'

export interface AdminUser {
  sub: string
  email: string
  status: string
  enabled: boolean
  created: string
  groups: string[]
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  
  const contentType = res.headers.get('content-type')
  let data: any = null
  
  if (contentType && contentType.includes('application/json')) {
    data = await res.json()
  }
  
  if (!res.ok) {
    const errorMessage = data?.message || await res.text() || `Request failed: ${res.status}`
    throw new Error(errorMessage)
  }
  
  return data as T
}

export const adminApi = {
  listUsers: () =>
    request<{ users: AdminUser[] }>('/users').then(d => d.users),

  getUser: (sub: string) =>
    request<{ user: AdminUser }>(`/users/${sub}`).then(d => d.user),

  createUser: (email: string, groups: string[]) =>
    request<{ message: string }>('/users', {
      method: 'POST',
      body: JSON.stringify({ email, groups }),
    }),

  updateUser: (sub: string, enabled: boolean) =>
    request<{ message: string }>(`/users/${sub}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  updateGroups: (sub: string, groups: string[]) =>
    request<{ message: string }>(`/users/${sub}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ groups }),
    }),

  disableUser: (sub: string) =>
    request<{ message: string }>(`/users/${sub}`, { method: 'DELETE' }),
}

export const ALL_APP_GROUPS = ['app:workout', 'app:trading', 'app:mdash', 'app:auto'] as const
export const APP_LABELS: Record<string, string> = {
  'app:workout': 'Workout',
  'app:trading': 'Trading',
  'app:mdash': 'Mail Dash',
  'app:auto': 'Auto Tracker',
}
export const APP_COLORS: Record<string, string> = {
  'app:workout': 'bg-green-100 text-green-800',
  'app:trading': 'bg-blue-100 text-blue-800',
  'app:mdash': 'bg-purple-100 text-purple-800',
  'app:auto': 'bg-orange-100 text-orange-800',
  'admin': 'bg-red-100 text-red-800',
}
